import { PromiseResponseProps } from "@/config/types/common";
import { TrendItem, TrendStatus, UserDenyStatus } from "@/config/types/trend";
import { TrendAuditLogItem, TrendAuditMsgItem } from "@/config/types/trend-audit";
import request, { requestCommunity } from "@/utils/request";

/**
 * 获取审核的数据
 * @see https://console-docs.apipost.cn/preview/9d5405b83e1477a2/c0ce8ee710614ba9?target_id=6d0c52fd-8f3a-4c73-a9df-4e5edb3614ed
 */
export const getTrendAuditData = (data: {
  startAt: string;
  endAt: string;
}): PromiseResponseProps<{
  allWait: number; //全部待审核
  meAudited: number; //本人已审核
  allAudited: number; //全部已审核
  completePercent: string; //审核完成率
}> => {
  return requestCommunity({
    url: "/admin/trends/v1/audit_data",
    method: "post",
    data,
  });
};

// 查询当前用户是否为审核员 http://apidoc.stac.fun/project/682/interface/api/65186
export const isLoginUserAuditAdmin = (): PromiseResponseProps<any> => {
  return request({
    url: "/community_audit/v1/is_login_user_audit_admin",
    method: "get",
  });
};

/**
 * 获取动态审核消息列表
 */
export const getTrendAuditMsgList = (data?: {
  pageNum?: number;
  pageSize?: number;
}): PromiseResponseProps<{
  pages: number;
  total: number;
  list: TrendAuditMsgItem[];
}> => {
  return requestCommunity({
    url: "/admin/trends/v1/notify_trends_audit_list",
    method: "post",
    data,
  });
};

/**
 * 获取待审核和已审核的数量
 */
export const getTrendAuditGroupCount = (): PromiseResponseProps<{
  wait: number;
  audited: number;
}> => {
  return requestCommunity({
    url: "/admin/trends/v1/trends_audit_group_count",
    method: "post",
    data: { startAt: "2023-12-20 00:00:00" },
  });
};

/**
 * 获取待审核的审核列表
 */
export const getTrendPendingList = (
  data?: any
): PromiseResponseProps<{ list: TrendItem[]; pages: number }> => {
  return requestCommunity({
    url: "/admin/trends/v1/trends_audit_list_wait",
    method: "post",
    data,
  });
};

/**
 * 获取已审核的审核列表
 */
export const getTrendFinishedList = (
  data?: any
): PromiseResponseProps<{ list: TrendItem[]; pages: number }> => {
  return requestCommunity({
    url: "/admin/trends/v1/trends_audit_list_finish",
    method: "post",
    data,
  });
};

/**
 * 获取审核动态的详情
 */
export const getTrendAuditDetail = (data: {
  trendsId: string;
}): PromiseResponseProps<TrendItem> => {
  return requestCommunity({
    url: "/admin/trends/v1/trends_audit_detail",
    method: "post",
    data,
  });
};

/**
 * 审核动态
 * @see https://console-docs.apipost.cn/preview/9d5405b83e1477a2/c0ce8ee710614ba9?target_id=b22711b0-1073-4477-840a-6af941f18427
 */
export const auditTrendDetail = (data: {
  auditStatus: TrendStatus; // 审核code: 0-待审核，1-审核通过，2-审核不通过（业务场景里，无法重新流转到「待审核」）
  auditReason: string; // 理由
  sendNoticeFlag?: "Y" | "N"; // 是否发送站内信
  trends: { trendsId: string }[];
}): PromiseResponseProps<null> => {
  return requestCommunity({
    url: "/admin/trends/v1/audit",
    method: "post",
    data: { sendNoticeFlag: "Y", ...data },
  });
};

/**
 * 查看审核日志
 */
export const getTrendAuditLog = (data: {
  bizId: string; //动态id
  bizObjType: string;
  pageNum: number; //页码
  pageSize: number; //页面大小
}): PromiseResponseProps<{
  list: TrendAuditLogItem[];
  pages: number;
}> => {
  return requestCommunity({
    // url: "/admin/trends/v1/view_trends_audit_list",
    url: "/admin/log/v1/list",
    method: "post",
    data,
  });
};

/**
 * 设置标签
 * https://console-docs.apipost.cn/preview/9d5405b83e1477a2/c0ce8ee710614ba9?target_id=a8562554-46a2-4d84-81a6-aeaf46166e31
 */
export const setTrendDetailTags = (data: {
  trendsIds: string[];
  auditTags: { name: string; code: string }[];
  overrides: boolean; // 是否覆盖
}) => {
  return requestCommunity({
    url: "/admin/trends/v1/set_audit_tags",
    method: "post",
    data,
  });
};

/**
 * 对违规用户进行处理
 * @see http://apidoc.stac.fun/project/42/interface/api/11406
 */
// 本期用不到
export const denyTrendUser = (data: {
  imid: string;
  status: UserDenyStatus;
  trends_id: string;
}) => {
  return requestCommunity({
    url: "/personal/user/account/v1/trends-status",
    method: "post",
    data,
  });
};

/**
 * 获取审核员列表(不包含本人) http://apidoc.stac.fun/project/682/interface/api/65191
 */
export const getAuditAdminList = () => {
  return request({
    url: "/community_audit/v1/get_list_without_login_user",
    method: "get",
  });
};

/**
 * 将审核中的动态移交给其他审核员
 */
export const transferTrendToUser = (data: {
  trendsId: string;
  newImId: string;
  newName: string;
}) => {
  return requestCommunity({
    url: "/admin/trends/v1/locked_transfer",
    method: "post",
    data,
  });
};
